import * as THREE from 'three';
import { CityState } from '../simulation/types';
import { AssetManager } from './AssetManager';

interface BuildingAnim {
  mesh: THREE.Object3D;
  targetScale: number;
  currentScale: number;
  state: 'spawn' | 'pulse' | 'shrink' | 'idle';
  pulseDirection?: 'up' | 'down';
  key: string;
}

export class BuildingRenderer {
  private scene: THREE.Scene;
  private buildingMeshes: Map<string, THREE.Object3D> = new Map();
  private animations: Map<string, BuildingAnim> = new Map();

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  public render(state: CityState) {
    const buildings = state.buildings;
    const activeKeys = new Set(Object.keys(buildings));

    const assetMgr = AssetManager.getInstance();
    const halfWidth = state.width / 2;
    const halfHeight = state.height / 2;

    for (const key of activeKeys) {
      const b = buildings[key];
      const worldX = b.x - halfWidth + 0.5;
      const worldZ = b.y - halfHeight + 0.5;

      const familyIndex = b.familyId.charCodeAt(b.familyId.length - 1) % 5;

      let mesh = this.buildingMeshes.get(key);
      if (!mesh) {
        // Create brand new building
        mesh = assetMgr.getBuildingMesh(b.zone, b.density, b.level, b.stage, familyIndex, b.familyId);
        mesh.position.set(worldX, 0, worldZ);
        
        // Start spawn scale at 0.1
        mesh.scale.set(0.1, 0.1, 0.1);
        this.scene.add(mesh);
        this.buildingMeshes.set(key, mesh);

        // Add spawn animation
        this.animations.set(key, {
          mesh,
          targetScale: 1.0,
          currentScale: 0.1,
          state: 'spawn',
          key
        });
      } else {
        // Check if mesh needs reconstruction
        const currentType = (mesh as any).buildingType;
        const currentStage = (mesh as any).buildingStage;
        const currentLevel = (mesh as any).buildingLevel;

        if (currentType !== b.zone || currentStage !== b.stage || currentLevel !== b.level) {
          // Recreate mesh
          const oldScale = mesh.scale.x;
          this.scene.remove(mesh);

          mesh = assetMgr.getBuildingMesh(b.zone, b.density, b.level, b.stage, familyIndex, b.familyId);
          mesh.position.set(worldX, 0, worldZ);
          
          // Carry over properties for tracking
          (mesh as any).buildingType = b.zone;
          (mesh as any).buildingStage = b.stage;
          (mesh as any).buildingLevel = b.level;

          mesh.scale.set(oldScale, oldScale, oldScale);
          this.scene.add(mesh);
          this.buildingMeshes.set(key, mesh);

          // Add upgrade pulse animation if moving from lower levels/stages
          this.animations.set(key, {
            mesh,
            targetScale: 1.0,
            currentScale: oldScale,
            state: 'pulse',
            pulseDirection: 'up',
            key
          });
        }
      }
    }

    // Demolish buildings that are no longer in state
    for (const key of Array.from(this.buildingMeshes.keys())) {
      if (!activeKeys.has(key)) {
        const mesh = this.buildingMeshes.get(key);
        if (mesh && !this.animations.has(key)) {
          // Start shrink-to-demolish animation
          this.animations.set(key, {
            mesh,
            targetScale: 0.0,
            currentScale: mesh.scale.x,
            state: 'shrink',
            key
          });
        }
      }
    }
  }

  /**
   * Updates building animations (elastic spawn, level-up pulse, shrink-on-demolish)
   */
  public update(_delta: number) {
    for (const [key, anim] of Array.from(this.animations.entries())) {
      const mesh = anim.mesh;

      if (anim.state === 'spawn') {
        // Elastic scale up LERP
        anim.currentScale = THREE.MathUtils.lerp(anim.currentScale, anim.targetScale, 0.15);
        mesh.scale.set(anim.currentScale, anim.currentScale, anim.currentScale);

        if (Math.abs(anim.currentScale - anim.targetScale) < 0.01) {
          mesh.scale.set(1, 1, 1);
          this.animations.delete(key);
        }
      } else if (anim.state === 'pulse') {
        // Upgrade pulse size up to 1.15, then shrink back to 1.0
        if (anim.pulseDirection === 'up') {
          anim.currentScale = THREE.MathUtils.lerp(anim.currentScale, 1.15, 0.25);
          mesh.scale.set(anim.currentScale, anim.currentScale, anim.currentScale);

          if (anim.currentScale >= 1.13) {
            anim.pulseDirection = 'down';
          }
        } else {
          anim.currentScale = THREE.MathUtils.lerp(anim.currentScale, 1.0, 0.2);
          mesh.scale.set(anim.currentScale, anim.currentScale, anim.currentScale);

          if (Math.abs(anim.currentScale - 1.0) < 0.01) {
            mesh.scale.set(1, 1, 1);
            this.animations.delete(key);
          }
        }
      } else if (anim.state === 'shrink') {
        // Shrink to zero before removing from scene
        anim.currentScale = THREE.MathUtils.lerp(anim.currentScale, 0.0, 0.18);
        mesh.scale.set(anim.currentScale, anim.currentScale, anim.currentScale);

        if (anim.currentScale <= 0.02) {
          this.scene.remove(mesh);
          this.buildingMeshes.delete(key);
          this.animations.delete(key);
        }
      }
    }
  }

  public clear() {
    this.buildingMeshes.forEach((mesh) => this.scene.remove(mesh));
    this.buildingMeshes.clear();
    this.animations.clear();
  }
}
